import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  IconButton,
  HStack,
  VStack,
  Flex,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  Spinner,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Card,
  CardBody,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
} from "@chakra-ui/react";
import {
  AddIcon,
  EditIcon,
  DeleteIcon,
  SearchIcon,
  CalendarIcon,
} from "@chakra-ui/icons";
import { useAppContext } from "../../../context/AppContext";
import {
  fetchConnections,
  deleteConnection,
} from "../../../services/AdminServices/ConnectionService";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { ConnectionModel } from "../../../models/workFlow/ConnectionModel";
import ConnectionModal from "./ConnectionModal"; // Import your ConnectionModal
import { ExternalLink, Workflow } from "lucide-react";
const Connection: React.FC = () => {
  const { accessToken, userData } = useAppContext();
  const { accounts } = useMsal();
  const name = accounts && accounts.length > 0 ? accounts[0].name : "";
  const [connectionList, setConnectionList] = useState<ConnectionModel[]>([]);
  const [filteredConnections, setFilteredConnections] = useState<
    ConnectionModel[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [selectedConnection, setSelectedConnection] =
    useState<ConnectionModel | null>(null);

  // Modal states
  const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);
  const [editingConnection, setEditingConnection] =
    useState<ConnectionModel | null>(null);

  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const loadConnections = async () => {
      if (!accessToken || !userData?.tenantID) return;

      setLoading(true);
      try {
        const connections = await fetchConnections(
          accessToken,
          userData.tenantID
        );
        console.log("Fetched connections:", connections);
        setConnectionList(connections);
        setFilteredConnections(connections);
      } catch (err) {
        console.error("Error loading connections", err);
        toast({
          title: "Error",
          description: "Failed to load connections. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    loadConnections();
  }, [accessToken, userData, toast]);

  // Filter connections based on search and type
  useEffect(() => {
    let filtered = connectionList;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (conn) =>
          conn.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          conn.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          conn.connectionType.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by connection type
    if (filterType !== "all") {
      filtered = filtered.filter((conn) => conn.connectionType === filterType);
    }

    setFilteredConnections(filtered);
  }, [searchTerm, filterType, connectionList]);

  // Handle Add Connection - Open modal for creating new connection
  const handleAddConnection = () => {
    setEditingConnection(null); // Set to null for create mode
    setIsConnectionModalOpen(true);
  };

  // Handle Edit Connection - Open modal for editing existing connection
  const handleEditConnection = (connection: ConnectionModel) => {
    setEditingConnection(connection); // Set connection for edit mode
    setIsConnectionModalOpen(true);
  };

  // Handle modal close
  const handleCloseConnectionModal = () => {
    setIsConnectionModalOpen(false);
    setEditingConnection(null);
  };

  // Handle successful connection creation/update
  const handleConnectionSuccess = async () => {
    // Reload connections after successful create/update
    if (!accessToken || !userData?.tenantID) return;

    try {
      const connections = await fetchConnections(
        accessToken,
        userData.tenantID
      );
      setConnectionList(connections);
      setFilteredConnections(connections);

      toast({
        title: "Success",
        description: editingConnection
          ? "Connection updated successfully!"
          : "Connection created successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Error reloading connections", err);
      toast({
        title: "Warning",
        description:
          "Connection saved but failed to reload list. Please refresh the page.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteConnection = (connection: ConnectionModel) => {
    setSelectedConnection(connection);
    onOpen();
  };

  const confirmDelete = async () => {
    if (
      !selectedConnection ||
      !userData ||
      userData.userID === undefined ||
      userData.tenantID === undefined
    ) {
      toast({
        title: "Error",
        description: "User data is missing. Cannot delete connection.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      const result = await deleteConnection(
        accessToken,
        selectedConnection.connectionId,
        userData.userID,
        userData.tenantID
      );

      if (result.success) {
        setConnectionList((prev) =>
          prev.filter(
            (conn) => conn.connectionId !== selectedConnection.connectionId
          )
        );

        toast({
          title: "Success",
          description: result.message,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: result.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete connection. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      onClose();
      setSelectedConnection(null);
    }
  };

  const getConnectionTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case "database":
        return "blue";
      case "api":
        return "green";
      case "file":
        return "purple";
      default:
        return "gray";
    }
  };

  const getConnectionTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case "database":
        return <CalendarIcon />;
      case "api":
        return <CalendarIcon />;
      default:
        return <CalendarIcon />;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="400px"
      >
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  return (
    <Box p={6} bg="gray.50" minH="100vh">
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Heading size="lg" color="gray.800">
              Connections
            </Heading>
            <Text color="gray.600">
              Manage your data connections and integrations
            </Text>
          </VStack>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={handleAddConnection}
            size="md"
            borderRadius="md"
            _hover={{ transform: "translateY(-1px)", boxShadow: "lg" }}
          >
            Add Connection
          </Button>
        </Flex>

        {/* Filters */}
        <Card>
          <CardBody>
            <Flex gap={4} wrap="wrap">
              <InputGroup maxW="300px">
                <InputLeftElement>
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search connections..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  bg="white"
                  borderRadius="md"
                />
              </InputGroup>
              <Select
                maxW="200px"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                bg="white"
                borderRadius="md"
              >
                <option value="all">All Types</option>
                <option value="database">Database</option>
                <option value="api">API</option>
                <option value="file">File</option>
              </Select>
            </Flex>
          </CardBody>
        </Card>

        {/* Connection List */}
        {/* Connection List */}
        <Card>
          <CardBody p={0}>
            <TableContainer>
              <Table variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th color="gray.600" fontWeight="600">
                      Name
                    </Th>
                    <Th color="gray.600" fontWeight="600">
                      Type
                    </Th>
                    <Th color="gray.600" fontWeight="600">
                      Action Type
                    </Th>
                    <Th color="gray.600" fontWeight="600">
                      Description
                    </Th>
                    <Th color="gray.600" fontWeight="600">
                      Redirect URL
                    </Th>
                    <Th color="gray.600" fontWeight="600">
                      Created
                    </Th>
                    <Th color="gray.600" fontWeight="600">
                      Updated
                    </Th>
                    <Th color="gray.600" fontWeight="600" width="120px">
                      Actions
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredConnections.length === 0 ? (
                    <Tr>
                      <Td colSpan={8} textAlign="center" py={8}>
                        <VStack spacing={3}>
                          <Text color="gray.500" fontSize="lg">
                            No connections found
                          </Text>
                          <Text color="gray.400" fontSize="sm">
                            {searchTerm || filterType !== "all"
                              ? "Try adjusting your search or filter"
                              : "Create your first connection to get started"}
                          </Text>
                        </VStack>
                      </Td>
                    </Tr>
                  ) : (
                    filteredConnections.map((connection) => (
                      <Tr
                        key={connection.connectionId}
                        _hover={{ bg: "gray.50" }}
                      >
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="600" color="gray.800">
                              {connection.name}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              ID: {connection.connectionId}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={getConnectionTypeColor(
                              connection.connectionType
                            )}
                            variant="subtle"
                            px={3}
                            py={1}
                            borderRadius="full"
                            display="flex"
                            alignItems="center"
                            gap={1}
                            width="fit-content"
                          >
                            {getConnectionTypeIcon(connection.connectionType)}
                            {connection.connectionType}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme="purple"
                            variant="outline"
                            px={3}
                            py={1}
                            borderRadius="full"
                          >
                            {connection.actionType}
                          </Badge>
                        </Td>
                        <Td>
                          <Text color="gray.600" noOfLines={2} maxW="200px">
                            {connection.description || "No description"}
                          </Text>
                        </Td>
                        <Td>
                          {connection.isSwaggerEnabled ? (
                            <HStack spacing={2}>
                              <Badge colorScheme="green" variant="subtle">
                                Swagger URL
                              </Badge>
                              {connection.swaggerUrl && (
                                <Tooltip label={connection.swaggerUrl}>
                                  <IconButton
                                    aria-label="Open Swagger"
                                    icon={<ExternalLink size={14} />}
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="blue"
                                    onClick={() => window.open(connection.swaggerUrl, '_blank')}
                                  />
                                </Tooltip>
                              )}
                            </HStack>
                          ) : (
                            <HStack spacing={2}>
                              <Badge colorScheme="green" variant="subtle">
                                API Url
                              </Badge>
                              {connection.apiUrl && (
                                <Tooltip label={connection.apiUrl}>
                                  <IconButton
                                    aria-label="Open Swagger"
                                    icon={<ExternalLink size={14} />}
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="blue"
                                    onClick={() => window.open(connection.apiUrl, '_blank')}
                                  />
                                </Tooltip>
                              )}
                            </HStack>

                          )}
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontSize="sm" color="gray.600">
                              {formatDate(connection.createdAt)}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              by {connection.createdBy}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontSize="sm" color="gray.600">
                              {formatDate(connection.updatedAt)}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              by {connection.updatedBy}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <HStack spacing={2}>
                            <Tooltip label="Execution Flow">
                              <IconButton
                                aria-label="Execution flow"
                                icon={<Workflow />}
                                size="sm"
                                colorScheme="blue"
                                variant="ghost"
                                onClick={() =>
                                  navigate(
                                    `/execution-flow-builder/${connection.connectionId}`,
                                    { state: { connection } }
                                  )
                                }
                                _hover={{ bg: "blue.50" }}
                              />
                            </Tooltip>

                            <Tooltip label="Edit Connection">
                              <IconButton
                                aria-label="Edit connection"
                                icon={<EditIcon />}
                                size="sm"
                                colorScheme="blue"
                                variant="ghost"
                                onClick={() => handleEditConnection(connection)}
                                _hover={{ bg: "blue.50" }}
                              />
                            </Tooltip>
                            <Tooltip label="Delete Connection">
                              <IconButton
                                aria-label="Delete connection"
                                icon={<DeleteIcon />}
                                size="sm"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() =>
                                  handleDeleteConnection(connection)
                                }
                                _hover={{ bg: "red.50" }}
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>

        {/* Stats */}
        <Card>
          <CardBody>
            <HStack spacing={8}>
              <VStack align="start" spacing={1}>
                <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                  {connectionList.length}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Total Connections
                </Text>
              </VStack>
              <VStack align="start" spacing={1}>
                <Text fontSize="2xl" fontWeight="bold" color="green.500">
                  {
                    connectionList.filter((c) => c.connectionType === "api")
                      .length
                  }
                </Text>
                <Text fontSize="sm" color="gray.600">
                  API Connections
                </Text>
              </VStack>
              <VStack align="start" spacing={1}>
                <Text fontSize="2xl" fontWeight="bold" color="purple.500">
                  {
                    connectionList.filter(
                      (c) => c.connectionType === "database"
                    ).length
                  }
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Database Connections
                </Text>
              </VStack>
            </HStack>
          </CardBody>
        </Card>
      </VStack>

      {/* Connection Modal for Create/Edit */}
      <ConnectionModal
        isOpen={isConnectionModalOpen}
        onClose={handleCloseConnectionModal}
        connection={editingConnection}
        onSuccess={handleConnectionSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Connection
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete the connection "
              {selectedConnection?.name}"? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default Connection;
